import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Box, Typography, TextField, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import ButtonComponent from "../../../Components/Common/button/Button"; // Adjust path
import ApiService from "../../../utils/fetch"; // Adjust path
import {
  setTemplateTreeData,
  setTemplateSelectedIds,
  setTemplateSearchQuery,
  setTemplateAppliedData,
  setTemplateExpandedItems,
} from "../../../redux/templateTreeSlice";

const transformData = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((bottler, bottlerIndex) => ({
    id: `bottler${bottlerIndex + 1}`,
    label: bottler.bottler || "Unknown Bottler",
    children: [
      {
        id: `all-bottler${bottlerIndex + 1}`,
        label: "All Country",
        isAllCheckbox: true,
      },
      ...(bottler.countries || []).map((country, countryIndex) => ({
        id: `country${bottlerIndex + 1}-${countryIndex + 1}`,
        label: country.country || "Unknown Country",
        children: [
          {
            id: `all-country${bottlerIndex + 1}-${countryIndex + 1}`,
            label: "All Customer",
            isAllCheckbox: true,
          },
          ...(country.customers || []).map((customer, customerIndex) => ({
            id: `customer${bottlerIndex + 1}-${countryIndex + 1}-${
              customerIndex + 1
            }`,
            label: customer.customerID || "Unknown Customer",
            customerID: customer.customerID || "",
            children: [
              {
                id: `all-plant${bottlerIndex + 1}-${countryIndex + 1}-${
                  customerIndex + 1
                }`,
                label: "All Plant",
                isAllCheckbox: true,
              },
              ...(customer.locationIds || []).map((locationId, plantIndex) => ({
                id: `plant${bottlerIndex + 1}-${countryIndex + 1}-${
                  customerIndex + 1
                }-${plantIndex + 1}`,
                label: ` ${locationId || "Unknown Plant"}`,
                locationId: locationId || "",
              })),
            ],
          })),
        ],
      })),
    ],
  }));
};

const getAllIds = (items) => {
  const ids = [];
  items.forEach((item) => {
    ids.push(item.id);
    if (item.children) {
      ids.push(...getAllIds(item.children));
    }
  });
  return ids;
};

const filterBottlersByPlant = (plantIds, products) => {
  return products
    .map((bottler) => {
      const filteredCountries = bottler.children
        .filter((country) => !country.isAllCheckbox)
        .map((country) => {
          const filteredCustomers = country.children
            .filter((customer) => !customer.isAllCheckbox)
            .map((customer) => {
              const matchingPlants = customer.children
                .filter((plant) => !plant.isAllCheckbox)
                .filter((plant) => plantIds.includes(plant.id));
              return matchingPlants.length > 0
                ? {
                    customerId: customer.customerID.split("-")[0],
                    locationId: matchingPlants.map(
                      (plant) => plant.locationId.split("-")[0]
                    ),
                  }
                : null;
            })
            .filter(Boolean);
          return filteredCustomers.length > 0
            ? { country: country.label, customerList: filteredCustomers }
            : null;
        })
        .filter(Boolean);
      return filteredCountries.length > 0
        ? { bottler: bottler.label, countryList: filteredCountries }
        : null;
    })
    .filter(Boolean)
    .flatMap((bottler) =>
      bottler.countryList.flatMap((country) =>
        country.customerList.flatMap((customer) =>
          customer.locationId.map((locationId) => ({
            locationId,
            customerId: customer.customerId,
          }))
        )
      )
    );
};

const filterTreeData = (items, query) => {
  if (!query) return items;
  return items
    .filter((item) => {
      const matches = item.label.toLowerCase().includes(query.toLowerCase());
      const children = item.children
        ? filterTreeData(item.children, query)
        : [];
      return matches || children.length > 0;
    })
    .map((item) => {
      const matches = item.label.toLowerCase().includes(query.toLowerCase());
      // If parent matches query, include ALL children (unfiltered)
      // If parent doesn't match, only include children that match
      const children = matches
        ? item.children
        : item.children ? filterTreeData(item.children, query) : [];
      return {
        ...item,
        children: children,
      };
    });
};

const getParentNode = (items, id) => {
  for (const item of items) {
    if (item.children) {
      if (item.children.some((child) => child.id === id)) {
        return item;
      }
      const parentNode = getParentNode(item.children, id);
      if (parentNode) return parentNode;
    }
  }
  return undefined;
};

const getAllParentIds = (items, id) => {
  const parentIds = [];
  let parent = getParentNode(items, id);
  while (parent) {
    parentIds.push(parent.id);
    parent = getParentNode(items, parent.id);
  }
  return parentIds;
};

const getAllChildIds = (item) => {
  const childIds = [];
  if (item.children) {
    item.children
      .filter((child) => !child.isAllCheckbox)
      .forEach((child) => {
        childIds.push(child.id);
        childIds.push(...getAllChildIds(child));
      });
  }
  return childIds;
};

const getAllDescendantPlantIds = (item) => {
  const plantIds = [];
  if (item.children) {
    item.children
      .filter((child) => !child.isAllCheckbox)
      .forEach((child) => {
        if (child.id.startsWith("plant")) {
          plantIds.push(child.id);
        } else {
          plantIds.push(...getAllDescendantPlantIds(child));
        }
      });
  }
  return plantIds;
};

const getSelectedIdsAndChildrenIds = (items, selectedIds) => {
  const selectedIdIncludingChildrenIds = new Set([...selectedIds]);
  for (const item of items) {
    if (selectedIds.includes(item.id)) {
      selectedIdIncludingChildrenIds.add(item.id);
      if (item.children) {
        const childrenIds = item.children
          .filter((child) => !child.isAllCheckbox)
          .map((child) => child.id);
        const childrenSelectedIds = getSelectedIdsAndChildrenIds(
          item.children,
          childrenIds
        );
        childrenSelectedIds.forEach((selectedId) =>
          selectedIdIncludingChildrenIds.add(selectedId)
        );
      }
    } else if (item.children) {
      const childrenSelectedIds = getSelectedIdsAndChildrenIds(
        item.children,
        selectedIds
      );
      childrenSelectedIds.forEach((selectedId) =>
        selectedIdIncludingChildrenIds.add(selectedId)
      );
    }
  }
  return [...Array.from(selectedIdIncludingChildrenIds)];
};

// const determineIdsToSet = (items, newIds, currentIds) => {
//   try {
//     if (!items || !Array.isArray(items) || !newIds || !Array.isArray(newIds)) {

//       return currentIds;
//     }

//     const isDeselectingNode = currentIds.length > newIds.length;
//     let idsToSet = new Set(newIds);

//     if (isDeselectingNode) {
//       const removed = currentIds.find((id) => !newIds.includes(id));
//       if (!removed) {

//         return [...idsToSet];
//       }

//       const removedItem = items
//         .flatMap((item) => [item, ...(item.children || [])])
//         .flatMap((item) => [item, ...(item.children || [])])
//         .flatMap((item) => [item, ...(item.children || [])])
//         .find((item) => item?.id === removed);

//       if (!removedItem) {

//         return [...idsToSet];
//       }

//       if (removedItem.isAllCheckbox) {
//         const parent = getParentNode(items, removed);
//         const allChildIds = parent ? getAllChildIds(parent) : [];
//         allChildIds.forEach((id) => idsToSet.delete(id));
//         idsToSet.delete(removed);
//       } else {
//         const childIdsToRemove = getSelectedIdsAndChildrenIds(items, [removed]);
//         childIdsToRemove.forEach((id) => idsToSet.delete(id));
//         const parentIdsToRemove = getAllParentIds(items, removed);
//         parentIdsToRemove.forEach((id) => {
//           const parent = items
//             .flatMap((item) => [item, ...(item.children || [])])
//             .flatMap((item) => [item, ...(item.children || [])])
//             .find((item) => item?.id === id);
//           if (
//             parent &&
//             !parent.isAllCheckbox &&
//             !parent.id.startsWith("bottler")
//           ) {
//             const parentPlantIds = getAllDescendantPlantIds(parent);
//             if (!parentPlantIds.some((plantId) => idsToSet.has(plantId))) {
//               idsToSet.delete(id);
//             }
//           }
//         });
//       }
//     } else {
//       const added = newIds.find((id) => !currentIds.includes(id));
//       if (!added) {

//         return [...idsToSet];
//       }

//       const addedItem = items
//         .flatMap((item) => [item, ...(item.children || [])])
//         .flatMap((item) => [item, ...(item.children || [])])
//         .flatMap((item) => [item, ...(item.children || [])])
//         .find((item) => item?.id === added);

//       if (!addedItem) {

//         return [...idsToSet];
//       }

//       if (addedItem.isAllCheckbox) {
//         const parent = getParentNode(items, added);
//         const allChildIds = parent ? getAllChildIds(parent) : [];
//         allChildIds.forEach((id) => idsToSet.add(id));
//         idsToSet.add(added);
//       } else {
//         const childIds = getSelectedIdsAndChildrenIds(items, [added]);
//         childIds.forEach((id) => idsToSet.add(id));
//         if (addedItem.id.startsWith("plant")) {
//           const parentIds = getAllParentIds(items, added);
//           parentIds.forEach((id) => {
//             if (id.startsWith("country") || id.startsWith("customer")) {
//               idsToSet.add(id);
//             }
//           });
//         }
//       }
//     }

//     items.forEach((bottler) => {
//       const allBottlerCheckbox = bottler.children?.find(
//         (child) => child.isAllCheckbox
//       );
//       const allBottlerPlantIds = getAllDescendantPlantIds(bottler);
//       if (allBottlerPlantIds.every((id) => idsToSet.has(id))) {
//         if (allBottlerCheckbox) idsToSet.add(allBottlerCheckbox.id);
//       } else {
//         if (allBottlerCheckbox) idsToSet.delete(allBottlerCheckbox.id);
//       }

//       bottler.children
//         ?.filter((country) => !country.isAllCheckbox)
//         .forEach((country) => {
//           const allCountryCheckbox = country.children?.find(
//             (child) => child.isAllCheckbox
//           );
//           const allCountryPlantIds = getAllDescendantPlantIds(country);
//           if (allCountryPlantIds.some((id) => idsToSet.has(id))) {
//             idsToSet.add(country.id);
//           } else {
//             idsToSet.delete(country.id);
//           }
//           if (allCountryPlantIds.every((id) => idsToSet.has(id))) {
//             if (allCountryCheckbox) idsToSet.add(allCountryCheckbox.id);
//             country.children
//               ?.filter((customer) => !customer.isAllCheckbox)
//               .forEach((customer) => {
//                 idsToSet.add(customer.id);
//                 const allPlantCheckbox = customer.children?.find(
//                   (child) => child.isAllCheckbox
//                 );
//                 if (allPlantCheckbox) idsToSet.add(allPlantCheckbox.id);
//               });
//           } else {
//             if (allCountryCheckbox) idsToSet.delete(allCountryCheckbox.id);
//             country.children
//               ?.filter((customer) => !customer.isAllCheckbox)
//               .forEach((customer) => {
//                 const allCustomerPlantIds = getAllDescendantPlantIds(customer);
//                 if (allCustomerPlantIds.some((id) => idsToSet.has(id))) {
//                   idsToSet.add(customer.id);
//                 } else {
//                   idsToSet.delete(customer.id);
//                 }
//                 if (allCustomerPlantIds.every((id) => idsToSet.has(id))) {
//                   const allPlantCheckbox = customer.children?.find(
//                     (child) => child.isAllCheckbox
//                   );
//                   if (allPlantCheckbox) idsToSet.add(allPlantCheckbox.id);
//                 } else {
//                   const allPlantCheckbox = customer.children?.find(
//                     (child) => child.isAllCheckbox
//                   );
//                   if (allPlantCheckbox) idsToSet.delete(allPlantCheckbox.id);
//                 }
//               });
//           }
//         });
//     });

//     const result = [...idsToSet];

//     return result;
//   } catch (error) {
//     console.error("Error in determineIdsToSet:", error);
//     return currentIds; // Fallback to prevent selection issues
//   }
// };
const determineIdsToSet = (items, newIds, currentIds) => {
  try {
    if (!items || !Array.isArray(items) || !newIds || !Array.isArray(newIds)) {
      return currentIds;
    }

    const isDeselectingNode = currentIds.length > newIds.length;
    let idsToSet = new Set(newIds);

    if (isDeselectingNode) {
      const removed = currentIds.find((id) => !newIds.includes(id));
      if (!removed) {
        return [...idsToSet];
      }

      const removedItem = items
        .flatMap((item) => [item, ...(item.children || [])])
        .flatMap((item) => [item, ...(item.children || [])])
        .flatMap((item) => [item, ...(item.children || [])])
        .find((item) => item?.id === removed);

      if (!removedItem) {
        return [...idsToSet];
      }

      if (removedItem.isAllCheckbox) {
        const parent = getParentNode(items, removed);
        const allChildIds = parent ? getAllChildIds(parent) : [];
        allChildIds.forEach((id) => idsToSet.delete(id));
        idsToSet.delete(removed);
      } else {
        const childIdsToRemove = getSelectedIdsAndChildrenIds(items, [removed]);
        childIdsToRemove.forEach((id) => idsToSet.delete(id));
        const parentIdsToRemove = getAllParentIds(items, removed);
        parentIdsToRemove.forEach((id) => {
          const parent = items
            .flatMap((item) => [item, ...(item.children || [])])
            .flatMap((item) => [item, ...(item.children || [])])
            .find((item) => item?.id === id);
          if (
            parent &&
            !parent.isAllCheckbox &&
            !parent.id.startsWith("bottler")
          ) {
            const parentPlantIds = getAllDescendantPlantIds(parent);
            if (!parentPlantIds.some((plantId) => idsToSet.has(plantId))) {
              idsToSet.delete(id);
            }
          }
        });
      }
    } else {
      const added = newIds.find((id) => !currentIds.includes(id));
      if (!added) {
        return [...idsToSet];
      }

      const addedItem = items
        .flatMap((item) => [item, ...(item.children || [])])
        .flatMap((item) => [item, ...(item.children || [])])
        .flatMap((item) => [item, ...(item.children || [])])
        .find((item) => item?.id === added);

      if (!addedItem) {
        return [...idsToSet];
      }

      if (addedItem.isAllCheckbox) {
        const parent = getParentNode(items, added);
        const allChildIds = parent ? getAllChildIds(parent) : [];
        allChildIds.forEach((id) => idsToSet.add(id));
        idsToSet.add(added);
      } else {
        const childIds = getSelectedIdsAndChildrenIds(items, [added]);
        childIds.forEach((id) => idsToSet.add(id));
        if (addedItem.id.startsWith("plant")) {
          const parentIds = getAllParentIds(items, added);
          parentIds.forEach((id) => {
            if (id.startsWith("country") || id.startsWith("customer")) {
              idsToSet.add(id);
            }
          });
        }
      }
    }

    // Update Bottler selection based on all plants being selected
    items.forEach((bottler) => {
      const allBottlerCheckbox = bottler.children?.find(
        (child) => child.isAllCheckbox
      );
      const allBottlerPlantIds = getAllDescendantPlantIds(bottler);
      const allPlantsSelected = allBottlerPlantIds.every((id) =>
        idsToSet.has(id)
      );

      // Only keep the bottler selected if all its plants are selected
      if (allPlantsSelected) {
        idsToSet.add(bottler.id);
        if (allBottlerCheckbox) idsToSet.add(allBottlerCheckbox.id);
      } else {
        idsToSet.delete(bottler.id);
        if (allBottlerCheckbox) idsToSet.delete(allBottlerCheckbox.id);
      }

      bottler.children
        ?.filter((country) => !country.isAllCheckbox)
        .forEach((country) => {
          const allCountryCheckbox = country.children?.find(
            (child) => child.isAllCheckbox
          );
          const allCountryPlantIds = getAllDescendantPlantIds(country);
          const hasCountryPlants = allCountryPlantIds.some((id) =>
            idsToSet.has(id)
          );

          if (hasCountryPlants) {
            idsToSet.add(country.id);
          } else {
            idsToSet.delete(country.id);
          }

          if (allCountryPlantIds.every((id) => idsToSet.has(id))) {
            if (allCountryCheckbox) idsToSet.add(allCountryCheckbox.id);
            country.children
              ?.filter((customer) => !customer.isAllCheckbox)
              .forEach((customer) => {
                idsToSet.add(customer.id);
                const allPlantCheckbox = customer.children?.find(
                  (child) => child.isAllCheckbox
                );
                if (allPlantCheckbox) idsToSet.add(allPlantCheckbox.id);
              });
          } else {
            if (allCountryCheckbox) idsToSet.delete(allCountryCheckbox.id);
            country.children
              ?.filter((customer) => !customer.isAllCheckbox)
              .forEach((customer) => {
                const allCustomerPlantIds = getAllDescendantPlantIds(customer);
                if (allCustomerPlantIds.some((id) => idsToSet.has(id))) {
                  idsToSet.add(customer.id);
                } else {
                  idsToSet.delete(customer.id);
                }
                if (allCustomerPlantIds.every((id) => idsToSet.has(id))) {
                  const allPlantCheckbox = customer.children?.find(
                    (child) => child.isAllCheckbox
                  );
                  if (allPlantCheckbox) idsToSet.add(allPlantCheckbox.id);
                } else {
                  const allPlantCheckbox = customer.children?.find(
                    (child) => child.isAllCheckbox
                  );
                  if (allPlantCheckbox) idsToSet.delete(allPlantCheckbox.id);
                }
              });
          }
        });
    });

    const result = [...idsToSet];

    return result;
  } catch (error) {
    console.error("Error in determineIdsToSet:", error);
    return currentIds; // Fallback to prevent selection issues
  }
};
const PlantSelectionTree = ({
  onApplySelection,
  title = "Plant Selection",
}) => {
  const dispatch = useDispatch();
  const {
    templateTreeData,
    templateSelectedIds,
    templateSearchQuery,
    templateExpandedItems,
  } = useSelector((state) => state.templateTree);
  const [loading, setLoading] = useState(false);
  // const [error, setError] = useState(null);
  const [selectedPart, setSelectedPart] = useState([]);

  // useEffect(() => {
  //   if (!templateTreeData.length) {
  //     countryData();
  //   }
  // }, [templateTreeData]);

    useEffect(() => {
    if (!templateTreeData.length) {
      countryData();
    }

    // Cleanup function to clear templateSelectedIds and templateAppliedData on unmount
    return () => {
      dispatch(setTemplateSelectedIds([]));
      dispatch(setTemplateAppliedData([]));
    };
  }, [templateTreeData, dispatch]);

  const countryData = async () => {
    setLoading(true);
    // setError(null);
    try {
      const response = await ApiService.get("/v1/bottler-selection/country");

      if (!Array.isArray(response.data)) {
        throw new Error("Invalid API response: Expected an array of bottlers");
      }
      const transformedData = transformData(response.data);

      dispatch(setTemplateTreeData(transformedData));
    } catch (error) {
      console.error(
        "API Error:",
        error.message,
        error.response?.status,
        error.response?.data
      );
      // setError("Failed to load data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    dispatch(setTemplateSearchQuery(event.target.value));
  };

  const handleSelectedItemsChange = (_event, ids) => {
    const newSelectedIds = determineIdsToSet(
      filteredData,
      ids,
      templateSelectedIds
    );
    setSelectedPart(newSelectedIds);
    dispatch(setTemplateSelectedIds(newSelectedIds));
  };

  const handleExpandedItemsChange = (_event, expandedItems) => {
    dispatch(setTemplateExpandedItems(expandedItems));
  };

  const applyAndClose = () => {
    const filteredItems = templateSelectedIds.filter((item) =>
      item.startsWith("plant")
    );
    const applyTreeViewData = filterBottlersByPlant(
      filteredItems,
      templateTreeData
    );

    dispatch(setTemplateAppliedData(applyTreeViewData));
    onApplySelection?.(applyTreeViewData);
  };

  const filteredData = filterTreeData(templateTreeData, templateSearchQuery);

  return (
    <div className="container">
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontStyle: "italic", color: "red" }} // mb: margin-bottom for spacing
        >
          Ensure you select a combination before uploading the file to set
          column positions.
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            pb: 0,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 400 }}>
            {title}
          </Typography>
          <ButtonComponent
            onClick={applyAndClose}
            maxWidth="150px"
            label="Apply"
            bgColor="black"
            color="white"
          />
        </Box>
        {loading && <Typography sx={{ p: 2 }}>Loading...</Typography>}
        {/* {error && (
          <Typography color="error" sx={{ p: 2 }}>
            {error}
          </Typography>
        )} */}
        <Box sx={{ flexShrink: 0, p: 2, pt: 0 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search"
            margin="normal"
            value={templateSearchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2, pt: 0 }}>
          <RichTreeView
            multiSelect
            checkboxSelection
            selectedItems={templateSelectedIds}
            onSelectedItemsChange={handleSelectedItemsChange}
            expandedItems={templateExpandedItems}
            onExpandedItemsChange={handleExpandedItemsChange}
            items={filteredData}
          />
        </Box>
      </Box>
    </div>
  );
};

export default PlantSelectionTree;
